import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminCourierIntegrations.css';

const COURIER_DISPLAY_NAMES = {
  dpd: 'DPD',
  omniva: 'Omniva',
  lp_express: 'LP Express',
  unisend: 'Unisend (Lietuvos paštas)',
};

function AdminCourierIntegrations() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editForm, setEditForm] = useState({
    is_enabled: false,
    is_test_mode: true,
    api_endpoint: '',
    api_key: '',
    api_secret: '',
    config_data: {}
  });
  const [unisendPlans, setUnisendPlans] = useState(null);
  const [unisendPlansLoading, setUnisendPlansLoading] = useState(false);

  const { data: integrations, isLoading } = useQuery({
    queryKey: ['admin-courier-integrations'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/courier-integrations');
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/courier-integrations/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-courier-integrations']);
      setEditingIntegration(null);
      notification.success('Kurjerio integracija sėkmingai atnaujinta');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Klaida atnaujinant kurjerio integraciją';
      const errorDetails = error.response?.data?.details || error.response?.data?.sqlMessage || '';
      notification.error(`${errorMessage}${errorDetails ? '\n\nDetalės: ' + errorDetails : ''}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => apiClient.post('/admin/courier-integrations', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-courier-integrations']);
      notification.success('Unisend integracija sukurta. Dabar galite ją konfigūruoti.');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant Unisend integraciją');
    },
  });

  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    
    // Paruošti config_data
    let configData = {};
    if (integration.config_data) {
      if (typeof integration.config_data === 'string') {
        try {
          configData = JSON.parse(integration.config_data);
        } catch (e) {
          configData = {};
        }
      } else if (typeof integration.config_data === 'object') {
        configData = integration.config_data;
      }
    }
    
    setEditForm({
      is_enabled: integration.is_enabled || false,
      is_test_mode: integration.is_test_mode !== undefined ? integration.is_test_mode : true,
      api_endpoint: integration.api_endpoint || '',
      api_key: integration.api_key || '',
      api_secret: integration.api_secret || '',
      config_data: configData
    });
  };

  const handleSave = () => {
    if (!editingIntegration) return;

    // Paruošti config_data
    let configDataToSend = null;
    if (editForm.config_data) {
      if (typeof editForm.config_data === 'string') {
        try {
          // Jei jau yra string, patikrinti ar validus JSON
          const parsed = JSON.parse(editForm.config_data);
          // Pašalinti tuščius laukus
          const cleaned = {};
          Object.keys(parsed).forEach(key => {
            const value = parsed[key];
            if (value !== null && value !== undefined && value !== '') {
              cleaned[key] = value;
            }
          });
          configDataToSend = Object.keys(cleaned).length > 0 ? cleaned : null;
        } catch (e) {
          // Jei ne validus JSON, naudoti kaip yra
          configDataToSend = editForm.config_data.trim() || null;
        }
      } else if (typeof editForm.config_data === 'object') {
        // Pašalinti tuščius laukus
        const cleaned = {};
        Object.keys(editForm.config_data).forEach(key => {
          const value = editForm.config_data[key];
          if (value !== null && value !== undefined && value !== '') {
            cleaned[key] = value;
          }
        });
        configDataToSend = Object.keys(cleaned).length > 0 ? cleaned : null;
      }
    }

    const dataToSend = {
      is_enabled: editForm.is_enabled,
      is_test_mode: editForm.is_test_mode,
      api_endpoint: editForm.api_endpoint || null,
      api_key: editForm.api_key || null,
      api_secret: editForm.api_secret || null,
      config_data: configDataToSend
    };

    updateMutation.mutate({
      id: editingIntegration.id,
      data: dataToSend
    });
  };

  const handleConfigChange = (key, value) => {
    setEditForm(prev => ({
      ...prev,
      config_data: {
        ...prev.config_data,
        [key]: value
      }
    }));
  };

  if (isLoading) {
    return <div className="admin-courier-integrations-page container">Kraunama...</div>;
  }

  const hasUnisend = integrations?.some((i) => i.courier_name === 'unisend');

  return (
    <div className="admin-courier-integrations-page container">
      <div className="integrations-header">
        <h1>Kurjerių integracijos</h1>
        {!hasUnisend && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => createMutation.mutate({
              courier_name: 'unisend',
              is_enabled: false,
              is_test_mode: true,
              api_endpoint: 'https://api-manosiuntostst.post.lt',
              config_data: {},
            })}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Kuriama...' : '+ Pridėti Unisend (Lietuvos paštas)'}
          </button>
        )}
      </div>

      <div className="integrations-list">
        {integrations?.map((integration) => (
          <div key={integration.id} className="integration-card">
            <div className="integration-info">
              <h3>{integration.display_name || COURIER_DISPLAY_NAMES[integration.courier_name] || integration.courier_name}</h3>
              <div className="integration-details">
                <div className="detail-item">
                  <span className="label">Statusas:</span>
                  <span className={`status-badge ${integration.is_enabled ? 'enabled' : 'disabled'}`}>
                    {integration.is_enabled ? 'Aktyvuota' : 'Neaktyvuota'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Režimas:</span>
                  <span className="value">
                    {integration.is_test_mode ? 'Test' : 'Live'}
                  </span>
                </div>
                {integration.api_endpoint && (
                  <div className="detail-item">
                    <span className="label">API Endpoint:</span>
                    <span className="value">{integration.api_endpoint}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="integration-actions">
              <button
                onClick={() => handleEdit(integration)}
                className="btn btn-primary"
              >
                Konfigūruoti
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Redagavimo modalas */}
      {editingIntegration && (
        <div className="modal-overlay" onClick={() => { setEditingIntegration(null); setUnisendPlans(null); }}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Konfigūruoti: {editingIntegration.display_name || COURIER_DISPLAY_NAMES[editingIntegration.courier_name] || editingIntegration.courier_name}</h2>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_enabled}
                  onChange={(e) => setEditForm({ ...editForm, is_enabled: e.target.checked })}
                />
                <span>Aktyvuoti integraciją</span>
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_test_mode}
                  onChange={(e) => setEditForm({ ...editForm, is_test_mode: e.target.checked })}
                />
                <span>Test režimas</span>
              </label>
            </div>

            {editingIntegration.courier_name !== 'unisend' && (
              <>
                <div className="form-group">
                  <label>API Endpoint</label>
                  <input
                    type="text"
                    value={editForm.api_endpoint}
                    onChange={(e) => setEditForm({ ...editForm, api_endpoint: e.target.value })}
                    className="input"
                    placeholder="https://api.example.com"
                  />
                </div>
                <div className="form-group">
                  <label>API Key</label>
                  <input
                    type="password"
                    value={editForm.api_key}
                    onChange={(e) => setEditForm({ ...editForm, api_key: e.target.value })}
                    className="input"
                    placeholder="Įveskite API key"
                  />
                </div>
                <div className="form-group">
                  <label>API Secret</label>
                  <input
                    type="password"
                    value={editForm.api_secret}
                    onChange={(e) => setEditForm({ ...editForm, api_secret: e.target.value })}
                    className="input"
                    placeholder="Įveskite API secret"
                  />
                </div>
              </>
            )}

            {editingIntegration.courier_name === 'unisend' && (
              <>
                <div className="form-group">
                  <label>API Endpoint (Lietuvos paštas)</label>
                  <input
                    type="text"
                    value={editForm.api_endpoint}
                    onChange={(e) => setEditForm({ ...editForm, api_endpoint: e.target.value })}
                    className="input"
                    placeholder="Test: https://api-manosiuntostst.post.lt | Prod: https://api-manosiuntos.post.lt"
                  />
                </div>
                <div className="form-group">
                  <label>Prisijungimo vardas (LP Express savitarna)</label>
                  <input
                    type="text"
                    value={editForm.config_data?.username || ''}
                    onChange={(e) => handleConfigChange('username', e.target.value)}
                    className="input"
                    placeholder="El. paštas arba vartotojo vardas"
                  />
                </div>
                <div className="form-group">
                  <label>Slaptažodis (LP Express savitarna)</label>
                  <input
                    type="password"
                    value={editForm.config_data?.password || ''}
                    onChange={(e) => handleConfigChange('password', e.target.value)}
                    className="input"
                    placeholder="Savitarnos slaptažodis"
                  />
                </div>
                <div className="form-group">
                  <label>Numatytasis siuntos planas (planCode)</label>
                  <input
                    type="text"
                    value={editForm.config_data?.default_plan_code || 'TERMINAL'}
                    onChange={(e) => handleConfigChange('default_plan_code', e.target.value)}
                    className="input"
                    placeholder="TERMINAL, HANDS, HANDS_EXPRESS, ..."
                  />
                </div>
                <div className="form-group">
                  <label>Numatytasis siuntos tipas (parcelType)</label>
                  <input
                    type="text"
                    value={editForm.config_data?.default_parcel_type || 'T2T'}
                    onChange={(e) => handleConfigChange('default_parcel_type', e.target.value)}
                    className="input"
                    placeholder="T2T, T2H, H2H, H2T, ..."
                  />
                </div>
                <div className="form-group">
                  <label>Numatytasis dydis (size)</label>
                  <select
                    value={editForm.config_data?.default_size || 'S'}
                    onChange={(e) => handleConfigChange('default_size', e.target.value)}
                    className="input"
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                  </select>
                </div>
                <div className="info-box">
                  <p><strong>Unisend (Lietuvos paštas)</strong></p>
                  <p>Pristatymo sąlygas ir kainodarą galite derinti tiesiogiai iš Lietuvos pašto serverio. API prieigą įjunkite <a href="https://lpexpress.lt/login" target="_blank" rel="noopener noreferrer">LP Express savitarnoje</a> → Integracijos → „Pradėti naudotis“.</p>
                  <p>Dokumentacija: <a href="https://www.post.lt/savitarna/api_doc.html#_overview" target="_blank" rel="noopener noreferrer">Unisend API dokumentacija (Overview)</a>, <a href="https://www.post.lt/instrukcija/woo-commerce/index.html" target="_blank" rel="noopener noreferrer">WooCommerce instrukcija</a>.</p>
                </div>
                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={unisendPlansLoading}
                    onClick={async () => {
                      setUnisendPlansLoading(true);
                      setUnisendPlans(null);
                      try {
                        const res = await apiClient.get('/admin/unisend/plans?receiverCountry=LT&senderCountry=LT');
                        setUnisendPlans(res.data.plans || []);
                        if ((res.data.plans || []).length === 0) notification.info('Planų nerasta arba patikrinkite prisijungimus.');
                      } catch (e) {
                        notification.error(e.response?.data?.details || e.response?.data?.error || 'Nepavyko gauti planų');
                        setUnisendPlans([]);
                      } finally {
                        setUnisendPlansLoading(false);
                      }
                    }}
                  >
                    {unisendPlansLoading ? 'Kraunama...' : 'Gauti siuntimo planus iš serverio'}
                  </button>
                  {unisendPlans && unisendPlans.length > 0 && (
                    <div style={{ marginTop: 8, maxHeight: 200, overflow: 'auto', fontSize: 13 }}>
                      <strong>Prieinami planai (LT→LT):</strong>
                      <ul style={{ marginTop: 4 }}>
                        {unisendPlans.slice(0, 20).map((p, i) => (
                          <li key={i}>{p.planCode || p.plan_code} / {p.parcelType || p.parcel_type} {p.size ? ` (${p.size})` : ''}</li>
                        ))}
                        {unisendPlans.length > 20 && <li>... ir dar {unisendPlans.length - 20}</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}

            {editingIntegration.courier_name === 'lp_express' && (
              <>
                <div className="form-group">
                  <label>Username (LP Express tiesioginiam API)</label>
                  <input
                    type="text"
                    value={editForm.config_data?.username || ''}
                    onChange={(e) => handleConfigChange('username', e.target.value)}
                    className="input"
                    placeholder="LP Express vartotojo vardas"
                  />
                </div>

                <div className="form-group">
                  <label>Password (LP Express tiesioginiam API)</label>
                  <input
                    type="password"
                    value={editForm.config_data?.password || ''}
                    onChange={(e) => handleConfigChange('password', e.target.value)}
                    className="input"
                    placeholder="LP Express slaptažodis"
                  />
                </div>

                <div className="info-box">
                  <p><strong>Pastaba:</strong></p>
                  <p>LP Express palaiko du integracijos būdus:</p>
                  <ul>
                    <li><strong>Cargoson API:</strong> Naudokite Cargoson endpoint (https://www.cargoson.com/api/v1) ir API key</li>
                    <li><strong>Tiesioginis LP Express API:</strong> Naudokite LP Express endpoint ir username/password</li>
                  </ul>
                </div>
              </>
            )}

            {editingIntegration.courier_name !== 'unisend' && (
              <div className="form-group">
                <label>Papildoma konfigūracija (JSON)</label>
                <textarea
                  value={editForm.config_data ? JSON.stringify(editForm.config_data, null, 2) : '{}'}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditForm({
                        ...editForm,
                        config_data: parsed
                      });
                    } catch (err) {}
                  }}
                  className="input"
                  rows="6"
                  placeholder='{"key": "value"}'
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Įveskite JSON formatu. Pavyzdys: {"{"}"username": "user", "password": "pass"{"}"}
                </small>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setEditingIntegration(null)}
                className="btn btn-secondary"
              >
                Atšaukti
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isLoading}
                className="btn btn-primary"
              >
                {updateMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCourierIntegrations;
